import React from 'react';
import { X, Minus, Square } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import './GatedTitleBar.css';

/**
 * The window's own buttons, shared by the full title bar and the bare strip
 * the sign-in gate keeps (GatedTitleBar). macOS draws them as traffic lights
 * on the left; Windows/Linux as minimise / maximise / close on the right.
 * Both are no-drag so the click reaches the button, not the window drag.
 */
export function MacTrafficLights({ ready, onClose, onMinimize, onMaximize }) {
  const { t } = useTranslation();
  return (
    <div
      className="mac-traffic-lights flex items-center flex-shrink-0"
      style={{ WebkitAppRegion: 'no-drag', gap: 8, paddingLeft: 5 }}
    >
      <button type="button" onClick={onClose} disabled={!ready} aria-label={t('titlebar.close')} className="mac-tl mac-tl-close" />
      <button type="button" onClick={onMinimize} disabled={!ready} aria-label={t('titlebar.minimize')} className="mac-tl mac-tl-minimize" />
      <button type="button" onClick={onMaximize} disabled={!ready} aria-label={t('titlebar.zoom')} className="mac-tl mac-tl-zoom" />
    </div>
  );
}

export function WindowButtons({ onClose, onMinimize, onMaximize }) {
  const { t } = useTranslation();
  const noDrag = { WebkitAppRegion: 'no-drag' };
  return (
    <>
      <button type="button" onClick={onMinimize} className="window-controls-button-small" aria-label={t('titlebar.minimize')} tabIndex={-1} style={noDrag}>
        <Minus size={12} />
      </button>
      <button type="button" onClick={onMaximize} className="window-controls-button-small" aria-label={t('titlebar.maximize')} tabIndex={-1} style={noDrag}>
        <Square size={10} />
      </button>
      <button
        type="button"
        onClick={onClose}
        className="window-controls-button-small window-controls-close-button-small"
        aria-label={t('titlebar.close')}
        tabIndex={-1}
        style={noDrag}
      >
        <X size={12} />
      </button>
    </>
  );
}

/**
 * While the desktop sign-in gate is up (platform/desktopGate.js) the gate
 * covers the whole window, title bar strip included (owner, 26.10.2026). This
 * strip floats over the gate's top edge with nothing on it but a drag region
 * and the window's own buttons: no project, environment, Mock, settings or
 * account controls are rendered, so none can be seen, clicked or tabbed to.
 * Transparent, so the gate's background shows through.
 */
export function GatedTitleBar({ isMac, inTauri, ready, onClose, onMinimize, onMaximize }) {
  const handlers = { onClose, onMinimize, onMaximize };
  return (
    <div
      className="app-titlebar app-titlebar--gated flex items-center select-none"
      data-testid="titlebar-gated"
      style={{ position: 'fixed', top: 0, left: 0, right: 0, height: 32, zIndex: 50, userSelect: 'none', background: 'transparent' }}
    >
      {isMac && inTauri && (
        <div className="flex px-2 items-center" style={{ WebkitAppRegion: 'no-drag' }}>
          <MacTrafficLights ready={ready} {...handlers} />
        </div>
      )}
      {/* The drag region: on macOS the traffic-light strip must stay out of it
          (WebKit would steal hover from AppKit), as in the full title bar. */}
      <div
        className="flex-1 self-stretch title-bar-drag-region"
        data-tauri-drag-region="true"
        data-testid="titlebar-drag-region"
        style={{ WebkitAppRegion: 'drag', minHeight: 32, cursor: isMac ? 'default' : 'move' }}
        aria-hidden
      />
      {!isMac && inTauri && (
        <div className="flex flex-shrink-0 items-center space-x-2 pr-2 tauri-window-controls title-bar-no-drag" style={{ WebkitAppRegion: 'no-drag' }}>
          <WindowButtons {...handlers} />
        </div>
      )}
    </div>
  );
}
